import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router';

import classNames from 'classnames';

import style from './CenteredAppNav.module.css';

import { useViewport } from '../../../hooks';
import { Flex } from '../../Flex';
import { Lightbox } from '../../Lightbox';
import { NavItem, type NavigationItem, isLinkItem } from '../../NavItem';
import { LogoWithMenuControl } from './LogoWithMenuControl';
import type { CenteredAppNavProps } from './types';

const MENU_ID = 'main-menu';

function ScrollableNavItem(props: NavigationItem) {
  const itemRef = useRef<HTMLAnchorElement>(null);

  const location = useLocation();
  const match = useRouteMatch(
    isLinkItem(props) && typeof props.to === 'string' ? props.to : []
  );

  useEffect(() => {
    if (match?.path && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'end', inline: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return isLinkItem(props) ? (
    <NavItem {...props} linkRef={itemRef} />
  ) : (
    <NavItem {...props} />
  );
}

export function CenteredAppNav({
  items,
  logo,
  logoTo = '/',
  menuItems,
  onMenuClose,
  onMenuOpen,
  rightItems,
}: CenteredAppNavProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { pathname } = useLocation();

  useEffect(() => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const viewport = useViewport();

  // if switching to a larger viewport, hide the menu
  useEffect(() => {
    if (viewport.lg) {
      setIsMenuOpen(false);
    }
  }, [viewport]);

  const navRef = useRef<HTMLDivElement>(null);
  const [lightboxOffset, setLightboxOffset] = useState(0);

  const updateLightboxOffset = () => {
    setLightboxOffset(navRef.current?.getBoundingClientRect().height ?? 0);
  };

  useLayoutEffect(() => {
    if (!navRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateLightboxOffset();
    });

    // initial paint, set the lightbox offset right away
    updateLightboxOffset();
    resizeObserver.observe(navRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleMenuClose = () => {
    setIsMenuOpen(false);
    onMenuClose?.();
    logoMenuRef.current?.focus();
  };

  const logoMenuRef = useRef<HTMLButtonElement>(null);

  return (
    <div
      className={classNames(style.root, { [style.menuOpen]: isMenuOpen })}
      style={{ height: lightboxOffset }}
    >
      <nav ref={navRef} className={style.nav}>
        <div role="list" aria-label="Navigation">
          <LogoWithMenuControl
            isOpen={isMenuOpen}
            logo={logo}
            logoTo={logoTo}
            menuId={MENU_ID}
            setIsMenuOpen={setIsMenuOpen}
            ref={logoMenuRef}
          />
          {!isMenuOpen && (
            <>
              <div role="presentation">
                <Flex px={{ xs: 20, lg: 0 }}>
                  {items.map((item, i) => (
                    <ScrollableNavItem key={i} {...item} activeStyle="line" />
                  ))}
                </Flex>
              </div>
              <div aria-hidden={!viewport.lg} role="presentation">
                {rightItems.map((item, i) => (
                  <ScrollableNavItem key={i} {...item} activeStyle="line" />
                ))}
              </div>
            </>
          )}
        </div>
      </nav>
      <Lightbox
        backdropClassName={style.menuBackdrop}
        className={style.menuLightbox}
        controlVisibility
        focusTraps={isMenuOpen ? [navRef] : []}
        isOpen={isMenuOpen}
        offsetTop={lightboxOffset}
        onOpen={onMenuOpen}
        onClosed={onMenuClose}
        onClose={handleMenuClose}
        role="presentation"
      >
        <div className={style.menu}>
          <div
            className={style.menuContainer}
            style={{ height: `calc(100dvh - ${lightboxOffset}px)` }}
          >
            <div
              id={MENU_ID}
              aria-expanded={isMenuOpen}
              role="list"
              aria-label="Navigation Menu"
              hidden={!isMenuOpen}
            >
              {menuItems.map((item, i) => (
                <div key={i} role="listitem" className={style.listItem}>
                  <NavItem
                    {...item}
                    orientation="vertical"
                    activeStyle="line"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Lightbox>
    </div>
  );
}
